import React, { useState, useEffect } from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { useMetaSelector } from '../meta/reducer';
import { GetStarted } from './get-started';
import { AssistantHero } from '../assistant/AssistantHero';
import { TabBar, TabContent, TabId, WidgetId, WIDGET_REGISTRY } from './tabs';
import { organisation as organisationApi, stroot } from '@birdi/js-sdk';

export const Dashboard: React.FC = () => {
  const theme: BirdiTheme = useTheme();
  const meta = useMetaSelector();
  const [activeTab, setActiveTab] = useState<TabId>('default');
  const [defaultTabWidgets, setDefaultTabWidgets] = useState<WidgetId[]>([]);
  const [isLoadingWidgets, setIsLoadingWidgets] = useState(true);
  const [isReorderMode, setIsReorderMode] = useState(false);
  // Ref to expose TabContent's open modal handler to TabBar
  const openAddWidgetModalRef = React.useRef<(() => void) | null>(null);


  const hasMapCommentsFeature = meta.features?.includes('map-comments');

  // Check if user can customize dashboard (Workspace Admin or Organisation Admin)
  const canCustomizeDashboard = meta.permissions?.includes('workspace:edit');

  const showUsageTab =
    Boolean(meta.permissions?.includes('organisation:workspace:create')) &&
    !meta.workspace;

  // Fetch dashboard widgets from API on mount
  useEffect(() => {
    const fetchDashboardWidgets = async () => {
      try {
        setIsLoadingWidgets(true);
        const res = await organisationApi.getDashboardWidgets(stroot('dshwgt'));
        if (res.body?.widgets) {
          // Validate widget IDs against the registry - filter out any invalid ones
          const validWidgetIds = Object.keys(WIDGET_REGISTRY) as WidgetId[];
          const validWidgets = res.body.widgets.filter(
            (id): id is WidgetId => {
              return typeof id === 'string' && validWidgetIds.includes(id as WidgetId);
            }
          );
          setDefaultTabWidgets(validWidgets);
        }
      } catch (error) {
        console.error('Error fetching dashboard widgets:', error);
        // On error, start with empty array
        setDefaultTabWidgets([]);
      } finally {
        setIsLoadingWidgets(false);
      }
    };

    fetchDashboardWidgets();
  }, []);

  React.useEffect(() => {
    if (!showUsageTab && activeTab === 'usage') {
      setActiveTab('default');
    }
  }, [showUsageTab, activeTab]);

  // Save widgets to API when they change
  const handleDefaultTabWidgetsChange = async (widgetIds: WidgetId[]) => {
    setDefaultTabWidgets(widgetIds);
    try {
      await organisationApi.updateDashboardWidgets(stroot('dshwgt'), widgetIds);
    } catch (error) {
      console.error('Error updating dashboard widgets:', error);
      // Optionally: show error message to user or revert state
    }
  };

  // Handle reorder mode
  const handleStartReorder = React.useCallback(() => {
    setIsReorderMode(true);
  }, []);

  const handleDoneReorder = React.useCallback(() => {
    // The actual save is handled by TabContent via window handler
    // This just exits reorder mode
    setIsReorderMode(false);
  }, []);

  // Prevent tab changes from resetting reorder mode
  const handleTabChange = React.useCallback((tabId: TabId) => {
    if (tabId === 'usage' && !showUsageTab) {
      return;
    }
    // Don't change tab if already on that tab
    if (tabId === activeTab) {
      return;
    }
    // Only change tab if not in reorder mode, or if switching away from default tab
    if (!isReorderMode) {
      setActiveTab(tabId);
    } else if (isReorderMode && tabId !== 'default' && activeTab === 'default') {
      // If switching away from default tab while in reorder mode, exit reorder mode first
      setIsReorderMode(false);
      setActiveTab(tabId);
    }
    // If in reorder mode and trying to stay on default, do nothing
  }, [isReorderMode, activeTab, showUsageTab]);

  return (
    <div
      css={css`
        background: ${theme.sand};
        min-height: 100vh;
        padding-bottom: 0.5em;
      `}
    >
      <div
        css={css`
          border-radius: 1em;
          margin: 0 auto;
          padding: 0 1em 0.5em 1em;
          max-width: 100%;
          overflow-x: hidden;
          box-sizing: border-box;
          @media (min-width: ${breakpoints.m}) {
            padding-left: 0.25em;
            padding-right: 0.5em;
          }
          @media (max-width: ${breakpoints.xs}) {
            padding: 0 0.75em 0.5em 0.75em;
          }
        `}
      >
        <GetStarted />
        <AssistantHero />
        <TabBar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          defaultTabWidgetsCount={defaultTabWidgets.length}
          isReorderMode={isReorderMode}
          onStartReorder={handleStartReorder}
          onDoneReorder={isReorderMode ? handleDoneReorder : undefined}
          onOpenAddWidgetModal={() => {
            if (openAddWidgetModalRef.current) {
              openAddWidgetModalRef.current();
            }
          }}
          canCustomizeDashboard={canCustomizeDashboard}
          showUsageTab={showUsageTab}
        />
        <TabContent
          activeTab={activeTab}
          hasMapCommentsFeature={hasMapCommentsFeature}
          defaultTabWidgets={defaultTabWidgets}
          onDefaultTabWidgetsChange={handleDefaultTabWidgetsChange}
          isLoadingWidgets={isLoadingWidgets}
          isReorderMode={isReorderMode}
          onDoneReorder={handleDoneReorder}
          onOpenAddWidgetModalRef={openAddWidgetModalRef}
          canCustomizeDashboard={canCustomizeDashboard}
          showUsageTab={showUsageTab}
        />
      </div>
    </div>
  );
};
